import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AuthPantallaCompleta } from '../components/onboarding/AuthPantallaCompleta';
import { type AuthMode } from '../components/onboarding/AuthForm';
import { useIsDesktop } from '../components/onboarding/useIsDesktop';
import { getPostLoginDest } from '../lib/postLoginDest';

/**
 * Registro a **pantalla completa** (mobile) — ADR-2026-07-30-registro-pantalla-completa.
 *
 * Es adonde lleva el CTA del carrusel de `/auth?mode=signup`. Antes ese CTA abría un
 * bottom sheet flotante sobre el carrusel; ahora ocupa toda la pantalla, con un back
 * arriba a la izquierda que vuelve al carrusel.
 *
 * Los cuatro modos (signup / login / forgot / confirmation) viven acá y cambian
 * in-place, sin abrir sheets.
 *
 * **Desktop (≥1024px) no usa esta ruta**: ahí la entrada es el split de `/auth`
 * (formulario a la izquierda + carrusel a la derecha), así que si el viewport es
 * desktop se redirige. Nadie linkea esta URL desde afuera.
 */

const TITLE_ID = 'registro-title';

export interface RegistroNavState {
  /** Ruta del carrusel del que se vino (`/auth?mode=signup`, con su query). */
  from?: string;
  /** Modo con el que abrir la pantalla (por defecto, "Crear cuenta"). */
  mode?: AuthMode;
}

export const Registro = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  const navState = (location.state as RegistroNavState | null) ?? {};
  // Sin `from` (deep-link directo) el back vuelve al carrusel general.
  const backTo = navState.from ?? '/auth?mode=signup';
  const [mode, setMode] = useState<AuthMode>(navState.mode ?? 'signup');
  const [postLoginDest] = useState(getPostLoginDest);

  // Desktop: esta pantalla no existe (el auth es el panel fijo del split de /auth).
  // Cubre tanto el deep-link desde una compu como el resize/rotación estando acá.
  useEffect(() => {
    if (isDesktop) navigate(backTo, { replace: true });
  }, [isDesktop, backTo, navigate]);

  // Si ya hay sesión, no tiene sentido mostrar el registro pre-alta → adentro.
  // (Sólo al montar, así no pisa la navegación que hace el propio formulario al
  // crear la sesión.)
  useEffect(() => {
    let activo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (activo && data.session) navigate(postLoginDest, { replace: true });
    });
    return () => {
      activo = false;
    };
  }, [navigate, postLoginDest]);

  const handleBack = () => {
    // Desde confirmación / login / olvidé la contraseña, el back vuelve primero al
    // registro (es la jerarquía que ve el usuario); recién ahí sale al carrusel.
    if (mode === 'forgot') {
      setMode('login');
      return;
    }
    if (mode === 'login' || mode === 'confirmation') {
      setMode('signup');
      return;
    }
    navigate(backTo);
  };

  return (
    <AuthPantallaCompleta
      mode={mode}
      onModeChange={setMode}
      titleId={TITLE_ID}
      onBack={handleBack}
    />
  );
};

export default Registro;
